import Phaser from "phaser";
import type UIPlugin from "phaser3-rex-plugins/templates/ui/ui-plugin.js";
import type RoundRectangle from "phaser3-rex-plugins/plugins/roundrectangle.js";
import type { BattlePayload, BattleReward, RewardEffect, WorldState } from "@/core";

const STAT_LABEL_RU: Record<string, string> = {
  strength: "Сила", dexterity: "Ловкость", intelligence: "Интеллект",
  wisdom: "Мудрость", constitution: "Телосложение",
};
function describeBattleEffect(effect: RewardEffect): string {
  switch (effect.type) {
    case "resource": return `+${effect.amount} ${effect.resourceType === "gold" ? "золота" : "маны"}`;
    case "equipment": return `снаряжение: ${effect.equipmentId}`;
    case "hp": return `+${effect.amount} HP`;
    case "stat": return `+${effect.delta} к ${STAT_LABEL_RU[effect.stat] ?? effect.stat}`;
    case "energy": return `+${effect.amount} энергии`;
    case "xp": return `+${effect.amount} опыта`
  }
}
import type { GameApp, BattleCtx } from "../app/GameApp";
import { REGISTRY_KEYS } from "../app/GameApp";
import {
  ENEMY_TEMPLATES, CLASS_DEFS, CARD_CATALOG,
  initBattle, playHeroCard, consumeHeroCard, canPlayHeroCard, endHeroTurn, enemyTurn, startHeroTurn,
  isGameOver, grantXp, rollLoot, getLootTable, cardsByClass, commonCards, computeEquipmentCards,
  statMod, computeCritThreshold, scaleEnemyToLevel,
  type BattleState, type BattleHeroSnapshot, type Stats,
  type HitResult, type DamageRollInfo,
} from "../core/roscha";
import { HpBar } from "@/ui/battle/HpBar";
import { BattleCard } from "@/ui/battle/BattleCard";
import { THEME, TEXT_STYLE } from "@/ui/battle/theme";
import { floatingFromEffect, floatingFromResource, floatingReputation, floatingXp } from "./floatingRewardMap";

interface DieDef {
  label: string;
  sides: number;
  finalValue: number;
  color: number;
  highlight?: boolean;
  fade?: boolean;
}

const FALLBACK_DECK = [
  "cutting_blade", "cutting_blade", "cutting_blade",
  "battle_cry", "iron_defense",
  "basic_strike", "basic_defend",
];

const HERO_CARD_W = 180;
const HERO_CARD_H = 280;
const ENEMY_CARD_W = 180;
const ENEMY_CARD_H = 260;

interface RexScene extends Phaser.Scene {
  rexUI: UIPlugin;
}

export class BattleScene extends Phaser.Scene {
  private app!: GameApp;
  private battlePayload!: BattlePayload;
  private state!: BattleState;

  private isProcessing = false;
  private diceOverlay?: Phaser.GameObjects.Container;
  private playedCardOverlay?: Phaser.GameObjects.Container;
  private resultOverlay?: Phaser.GameObjects.Container;

  private bgGradient?: Phaser.GameObjects.Graphics;
  private enemyPanel?: Phaser.GameObjects.Container;
  private enemyPanelBg?: RoundRectangle;
  private enemyHp?: HpBar;
  private enemyStatsTableLayer?: Phaser.GameObjects.Container;
  private enemyHandLayer?: Phaser.GameObjects.Container;
  private enemyStatusLayer?: Phaser.GameObjects.Container;
  private heroStatusLayer?: Phaser.GameObjects.Container;
  private statusTooltip?: Phaser.GameObjects.Container;

  private heroPanel?: Phaser.GameObjects.Container;
  private heroPanelBg?: RoundRectangle;
  private heroHp?: HpBar;
  private heroDerivedLayer?: Phaser.GameObjects.Container;
  private heroStatsLayer?: Phaser.GameObjects.Container;
  private heroManaLayer?: Phaser.GameObjects.Container;
  private heroTurnText?: Phaser.GameObjects.Text;

  private handLayer?: Phaser.GameObjects.Container;
  private logContainer?: Phaser.GameObjects.Container;
  private logTextItems: Phaser.GameObjects.Text[] = [];
  private endTurnBtn?: Phaser.GameObjects.Container;
  private retreatBtn?: Phaser.GameObjects.Container;

  constructor() {
    super("BattleScene");
  }

  private lastPayloadRef: BattlePayload | null = null;

  preload() {
    if (!this.textures.exists("battleBg")) {
      this.load.image("battleBg", "/battle-bg.png");
    }
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;

    const ingest = (ctx: BattleCtx | null) => {
      if (!ctx) return;
      if (this.lastPayloadRef && this.lastPayloadRef !== ctx.payload) {
        this.lastPayloadRef = ctx.payload;
        this.battlePayload = ctx.payload;
        this.scene.restart();
        return;
      }
      this.lastPayloadRef = ctx.payload;
      this.battlePayload = ctx.payload;
      this.startBattle();
    };

    ingest(this.registry.get(REGISTRY_KEYS.currentBattle) as BattleCtx | null);

    const onChange = (_parent: unknown, value: BattleCtx | null) => ingest(value);
    this.registry.events.on(`changedata-${REGISTRY_KEYS.currentBattle}`, onChange);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.registry.events.off(`changedata-${REGISTRY_KEYS.currentBattle}`, onChange);
      this.shutdown();
    });
  }

  private startBattle() {
    const world = this.app.getWorldState();
    if (!world) return this.app.completeCurrentEvent();
    const template = ENEMY_TEMPLATES[this.battlePayload.enemyTemplateId];
    if (!template) return this.app.completeCurrentEvent();
    let enemy = this.battlePayload.enemyLevel != null
      ? scaleEnemyToLevel(template, this.battlePayload.enemyLevel)
      : template;
    if (this.battlePayload.enemyMaxHp != null) {
      enemy = { ...enemy, maxHp: this.battlePayload.enemyMaxHp };
    }

    this.isProcessing = false;
    this.state = initBattle(this.buildHeroSnapshot(world), enemy);

    this.buildBackground();
    this.buildEnemyPanel();
    this.buildHeroPanel();
    this.buildLog();
    this.buildButtons();
    this.buildHand();
    this.refreshAll();
    if (!this.listenersBound) {
      this.scale.on("resize", this.onResize, this);
      this.input.on("pointermove", this.handlePointerMove, this);
      this.listenersBound = true;
    }
    this.maybeAutoEndTurn();
  }

  private listenersBound = false;

  sortHand() {
    if (!this.handLayer) return;
    this.handLayer.list.sort((a, b) => {
      const da = (a as Phaser.GameObjects.Container).depth;
      const db = (b as Phaser.GameObjects.Container).depth;
      if (da !== db) return da - db;
      const ia = (a as BattleCard).handIndex ?? 0;
      const ib = (b as BattleCard).handIndex ?? 0;
      return ia - ib;
    });
  }

  private handlePointerMove(p: Phaser.Input.Pointer) {
    if (!this.handLayer) return;
    const cards = this.handLayer.list as BattleCard[];
    let hoveredAny = false;
    for (const c of cards) {
      const b = c.getBounds();
      if (b.contains(p.x, p.y)) { hoveredAny = true; break; }
    }
    if (!hoveredAny) {
      for (const c of cards) c.snapUnhover();
    }
  }

  private buildHeroSnapshot(world: WorldState): BattleHeroSnapshot {
    const h = world.hero as Partial<WorldState["hero"]> & WorldState["hero"];
    const classDef = h.classId ? CLASS_DEFS[h.classId] : CLASS_DEFS["warrior"];
    const defaultStats: Stats = classDef.startingStats;
    const stats: Stats = h.stats ?? defaultStats;
    const baseDeck = (h.deck ?? []).filter((id) => CARD_CATALOG[id] != null);
    const equipCards = h.equipment ? computeEquipmentCards({ equipment: h.equipment }).filter((id) => CARD_CATALOG[id] != null) : [];
    const combined = [...baseDeck, ...equipCards];
    return {
      name: "Герой",
      classId: classDef.id,
      domainId: h.domainId ?? classDef.domainIds[0],
      stats,
      hp: h.hp ?? 30,
      maxHp: h.maxHp ?? 30,
      armor: h.armor ?? 2,
      evasion: h.evasion ?? 12,
      keyStat: classDef.keyStat,
      hitMod: statMod(stats.dexterity),
      critThreshold: computeCritThreshold({ equipment: h.equipment ?? { head: null, body: null, hands: null, weapon: null, accessory: null } }),
      level: h.level ?? 1,
      deck: combined.length >= 3 ? combined : FALLBACK_DECK,
      fearAccumulated: h.fearAccumulated ?? 0,
      reputationUpgrades: h.reputationUpgrades ?? {},
      difficulty: h.difficulty ?? "normal",
    };
  }

  private buildBackground() {
    const w = this.scale.width;
    const h = this.scale.height;

    if (this.textures.exists("battleBg")) {
      const img = this.add.image(w / 2, h / 2, "battleBg");
      const tex = this.textures.get("battleBg").getSourceImage() as HTMLImageElement;
      const scale = Math.max(w / tex.width, h / tex.height);
      img.setScale(scale).setDepth(-100);

      const overlay = this.add.graphics();
      overlay.fillStyle(0x000000, 0.55).fillRect(0, 0, w, h);
      overlay.setDepth(-99);
    } else {
      this.bgGradient = this.add.graphics();
      this.bgGradient.fillStyle(THEME.bgDeep, 1).fillRect(0, 0, w, h);
    }
  }

  private buildEnemyPanel() {
    const rex = (this as unknown as RexScene).rexUI;
    const enemy = this.state.enemy;

    const panelW = 380;
    const panelH = 230;
    const px = 30;
    const py = 30;
    const cx = px + panelW / 2;
    const cy = py + panelH / 2;

    this.enemyPanelBg = rex.add.roundRectangle(cx, cy, panelW, panelH, 14, THEME.bgPanel, 0.95)
      .setStrokeStyle(1, THEME.borderAccent, 0.55);

    const name = this.add.text(cx, py + 18, enemy.name.toUpperCase(), {
      ...TEXT_STYLE.display, fontSize: "26px", fontStyle: "bold",
    }).setOrigin(0.5, 0);
    name.setLetterSpacing(4);

    const lvl = this.add.text(cx, py + 56, `Уровень ${enemy.level}`, {
      ...TEXT_STYLE.small, fontSize: "12px",
    }).setOrigin(0.5, 0);
    lvl.setLetterSpacing(1.5);

    this.enemyHp = new HpBar({
      scene: this, x: cx, y: py + 96, width: panelW - 40,
      label: "HP", hp: enemy.hp, maxHp: enemy.maxHp, color: THEME.fear,
    });

    this.enemyStatsTableLayer = this.add.container(px + 20, py + 120);
    this.enemyStatusLayer = this.add.container(px + 10, py + panelH + 8);

    this.enemyPanel = this.add.container(0, 0, [name, lvl]);

    this.enemyHandLayer = this.add.container(0, 0);
    this.refreshEnemyHand();
  }

  private buildHeroPanel() {
    const rex = (this as unknown as RexScene).rexUI;
    const hero = this.state.hero;

    const panelW = 380;
    const panelH = 290;
    const px = 30;
    const heroCardCenterY = this.scale.height - HERO_CARD_H / 2 - 30;
    const py = heroCardCenterY - panelH / 2;
    const cx = px + panelW / 2;
    const cy = py + panelH / 2;

    this.heroPanelBg = rex.add.roundRectangle(cx, cy, panelW, panelH, 14, THEME.bgPanel, 0.95)
      .setStrokeStyle(1, THEME.borderAccent, 0.55);

    const name = this.add.text(cx, py + 12, hero.name.toUpperCase(), {
      ...TEXT_STYLE.display, fontSize: "20px", fontStyle: "bold",
    }).setOrigin(0.5, 0);
    name.setLetterSpacing(3);

    this.heroHp = new HpBar({
      scene: this, x: cx, y: py + 48, width: panelW - 40,
      label: "HP", hp: hero.hp, maxHp: hero.maxHp, color: THEME.success,
    });

    this.heroDerivedLayer = this.add.container(px + 20, py + 74);
    this.heroStatsLayer = this.add.container(px + 20, py + 130);
    this.heroManaLayer = this.add.container(px + 20, py + 220);

    this.heroStatusLayer = this.add.container(px + 10, py - 34);

    this.heroTurnText = this.add.text(cx, py + panelH - 20, "", {
      ...TEXT_STYLE.small, fontSize: "11px", fontStyle: "italic",
    }).setOrigin(0.5, 0);
    this.heroTurnText.setLetterSpacing(0.8);

    this.heroPanel = this.add.container(0, 0, [name, this.heroTurnText]);
  }

  private buildLog() {
    const rex = (this as unknown as RexScene).rexUI;
    const w = this.scale.width;
    const panelW = 400;
    const panelH = 360;
    const x = w - panelW / 2 - 30;
    const y = 30 + panelH / 2;

    rex.add.roundRectangle(x, y, panelW, panelH, 14, THEME.bgPanel, 0.95)
      .setStrokeStyle(1, THEME.borderAccent, 0.55);

    const title = this.add.text(x, y - panelH / 2 + 16, "ХРОНИКА БОЯ", {
      ...TEXT_STYLE.small, fontSize: "13px", fontStyle: "bold",
      color: `#${THEME.textAccent.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5, 0);
    title.setLetterSpacing(2);

    this.logContainer = this.add.container(x - panelW / 2 + 16, y - panelH / 2 + 48);
  }

  private buildButtons() {
    const w = this.scale.width;
    const h = this.scale.height;

    const btnH = 52;
    const endW = 220;
    const retW = 180;

    const endCX = w - 40 - endW / 2;
    const cy = h - 32 - btnH / 2;
    this.endTurnBtn = this.makeButton("Конец хода", endW, btnH, true, () => this.onEndTurn());
    this.endTurnBtn.setPosition(endCX, cy);

    const retCX = endCX - endW / 2 - 16 - retW / 2;
    this.retreatBtn = this.makeButton("Отступить", retW, btnH, false, () => this.onRetreat());
    this.retreatBtn.setPosition(retCX, cy);
  }

  private buildHand() {
    if (this.handLayer) this.handLayer.destroy();
    this.handLayer = this.add.container(0, 0);
    this.refreshHand();
  }

  private refreshAll() {
    this.refreshEnemy();
    this.refreshHero();
    this.refreshHand();
    this.refreshEnemyHand();
    this.refreshLog();
    this.refreshButtons();
  }

  private statusEffectLabel(e: { id: string; stacks: number; ttl: number; dieSides?: number; stat?: string; delta?: number }): { short: string; full: string } {
    const statShort: Record<string, string> = { strength: "СИЛ", dexterity: "ЛОВ", intelligence: "ИНТ", wisdom: "МУД", constitution: "ТЕЛ" };
    const statFull: Record<string, string> = { strength: "Силе", dexterity: "Ловкости", intelligence: "Интеллекту", wisdom: "Мудрости", constitution: "Телосложению" };
    const dur = e.ttl >= 99 ? "до конца боя" : `${e.ttl} ход${e.ttl === 1 ? "" : e.ttl < 5 ? "а" : "ов"}`;
    switch (e.id) {
      case "poison":     return { short: "Яд",            full: `Яд. В начале хода жертвы наносит 1d${e.dieSides ?? 4} урона. Осталось: ${dur}.` };
      case "bleed":      return { short: "Кровотечение",  full: `Кровотечение. В начале хода жертвы наносит 1d${e.dieSides ?? 4} урона. Осталось: ${dur}.` };
      case "hitBoost":   return { short: "Меткость",      full: `Шанс попадания увеличен на +${e.stacks}. Осталось: ${dur}.` };
      case "damageDie":  return { short: "Усиление",      full: `Каждая атака получает +${e.stacks}d${e.dieSides ?? 4} к урону. Осталось: ${dur}.` };
      case "statDebuff": {
        const sn = statShort[e.stat ?? ""] ?? e.stat ?? "?";
        const sf = statFull[e.stat ?? ""] ?? e.stat ?? "?";
        const d = e.delta ?? 0;
        return { short: `${d > 0 ? "+" : ""}${d} ${sn}`, full: `${d > 0 ? "Бонус" : "Дебафф"} к ${sf}: ${d > 0 ? "+" : ""}${d}. Осталось: ${dur}.` };
      }
      case "unbreakable": return { short: "Несокрушимость", full: `Несокрушимость. Армор не понижается ни от ударов, ни от пробивающих эффектов. Осталось: ${dur}.` };
      case "stun":       return { short: `Оглушение ${e.stacks}`, full: `Оглушение. Восстановление Надежды снижено на ${e.stacks}. Каждый ход ослабляется на 1.` };
      default: return { short: e.id, full: e.id };
    }
  }

  private renderStatusChips(layer: Phaser.GameObjects.Container | undefined, list: { id: string; stacks: number; ttl: number; dieSides?: number; stat?: string; delta?: number }[] | undefined, maxWidth: number) {
    if (!layer) return;
    layer.removeAll(true);
    if (!list || list.length === 0) return;

    let lineX = 0, lineY = 0;
    const lineH = 22;
    const gap = 6;
    const padX = 10, padY = 4;

    for (const eff of list) {
      const { short, full } = this.statusEffectLabel(eff);
      const text = this.add.text(0, 0, short, {
        ...TEXT_STYLE.small, fontSize: "11px", fontStyle: "bold",
        color: `#${THEME.textPrimary.toString(16).padStart(6, "0")}`,
        backgroundColor: "#1a1620",
        padding: { left: padX, right: padX, top: padY, bottom: padY },
      });
      const chipW = text.width;
      if (lineX + chipW > maxWidth && lineX > 0) {
        lineX = 0;
        lineY += lineH + 4;
      }
      text.setPosition(lineX, lineY);
      text.setInteractive({ useHandCursor: true });
      const bounds = { x: lineX, y: lineY, w: chipW, h: text.height };
      text.on("pointerover", () => this.showStatusTooltip(layer.x + bounds.x, layer.y + bounds.y, bounds.h, full));
      text.on("pointerout", () => this.hideStatusTooltip());
      layer.add(text);
      lineX += chipW + gap;
    }
  }

  private showStatusTooltip(chipX: number, chipY: number, _chipH: number, text: string) {
    this.hideStatusTooltip();
    const rex = (this as unknown as RexScene).rexUI;
    const padX = 12, padY = 8;
    const textObj = this.add.text(0, 0, text, {
      ...TEXT_STYLE.small, fontSize: "12px",
      color: `#${THEME.textPrimary.toString(16).padStart(6, "0")}`,
      wordWrap: { width: 260 },
    }).setOrigin(0.5);
    const tw = textObj.width + padX * 2;
    const th = textObj.height + padY * 2;
    const bg = rex.add.roundRectangle(0, 0, tw, th, 8, THEME.bgPanel, 0.98)
      .setStrokeStyle(1, THEME.borderAccent, 0.9);
    const screenMargin = 12;
    const desiredX = chipX + tw / 2;
    const tooltipX = Math.max(tw / 2 + screenMargin, Math.min(this.scale.width - tw / 2 - screenMargin, desiredX));
    const desiredY = chipY - th / 2 - 4;
    const tooltipY = Math.max(th / 2 + screenMargin, desiredY);
    const c = this.add.container(tooltipX, tooltipY, [bg as unknown as Phaser.GameObjects.GameObject, textObj]).setDepth(9000);
    this.statusTooltip = c;
  }

  private hideStatusTooltip() {
    if (this.statusTooltip) {
      this.statusTooltip.destroy();
      this.statusTooltip = undefined;
    }
  }

  private refreshEnemy() {
    const e = this.state.enemy;
    this.enemyHp?.setHp(e.hp, e.maxHp);
    this.renderStatTable(this.enemyStatsTableLayer, 340, [
      { label: "Броня", value: String(e.armor) },
      { label: "Уклонение", value: String(e.evasion) },
      { label: "Уровень", value: String(e.level) },
      { label: "Надежда", value: `${e.mana.hope}/${e.mana.maxHope}` },
    ]);
    const enemyChips = [...(e.statusEffects ?? [])] as { id: string; stacks: number; ttl: number; dieSides?: number; stat?: string; delta?: number }[];
    this.renderStatusChips(this.enemyStatusLayer, enemyChips, 360);
  }

  private refreshHero() {
    const hero = this.state.hero;
    this.heroHp?.setHp(hero.hp, hero.maxHp);

    this.renderStatTable(this.heroDerivedLayer, 340, [
      { label: "Броня", value: String(hero.armor) },
      { label: "Уклонение", value: String(hero.evasion) },
      { label: "Уровень", value: String(hero.level) },
    ]);

    const debuffSums: Record<string, number> = {};
    for (const eff of hero.statusEffects ?? []) {
      if (eff.id !== "statDebuff" || !eff.stat) continue;
      debuffSums[eff.stat] = (debuffSums[eff.stat] ?? 0) + (eff.delta ?? 0) * eff.stacks;
    }
    const statRow = (label: string, key: keyof Stats) => {
      const base = hero.stats[key] ?? 0;
      const delta = debuffSums[key] ?? 0;
      const eff = base + delta;
      const color = delta < 0 ? "#d96a6a" : delta > 0 ? "#7fd47f" : `#${THEME.textPrimary.toString(16).padStart(6, "0")}`;
      return { label, value: String(eff), valueColor: color };
    };
    this.renderStatTable(this.heroStatsLayer, 340, [
      statRow("Сила", "strength"),
      statRow("Ловкость", "dexterity"),
      statRow("Интеллект", "intelligence"),
      statRow("Мудрость", "wisdom"),
      statRow("Телосложение", "constitution"),
    ]);

    this.renderStatTable(this.heroManaLayer, 340, [
      { label: "Надежда", value: `${hero.mana.hope}/${hero.mana.maxHope}`, valueColor: `#${THEME.success.toString(16).padStart(6, "0")}` },
      { label: "Страх", value: `${hero.mana.fear}/${hero.mana.maxFear}`, valueColor: `#${THEME.fear.toString(16).padStart(6, "0")}` },
    ]);

    const heroChips = [...(hero.statusEffects ?? [])] as { id: string; stacks: number; ttl: number; dieSides?: number; stat?: string; delta?: number }[];
    this.renderStatusChips(this.heroStatusLayer, heroChips, 360);
    this.heroTurnText?.setText(`Ход ${this.state.turn} · ${this.state.activeSide === "hero" ? "Твой ход" : "Ход врага"}`);
  }

  private renderStatTable(
    layer: Phaser.GameObjects.Container | undefined,
    width: number,
    rows: Array<{ label: string; value: string; valueColor?: string }>,
  ) {
    if (!layer) return;
    layer.removeAll(true);
    const rowH = 16;
    rows.forEach((row, i) => {
      const y = i * rowH;
      const left = this.add.text(0, y, row.label, {
        ...TEXT_STYLE.small, fontSize: "12px",
        color: `#${THEME.textMuted.toString(16).padStart(6, "0")}`,
      }).setOrigin(0, 0);
      left.setLetterSpacing(0.5);
      const right = this.add.text(width, y, row.value, {
        ...TEXT_STYLE.display, fontSize: "12px", fontStyle: "bold",
        color: row.valueColor ?? `#${THEME.textPrimary.toString(16).padStart(6, "0")}`,
      }).setOrigin(1, 0);
      right.setLetterSpacing(0.8);
      layer.add([left, right]);
    });
  }

  private refreshLog() {
    if (!this.logContainer) return;
    for (const t of this.logTextItems) t.destroy();
    this.logTextItems = [];

    const maxLines = 12;
    const recent = this.state.log.slice(-maxLines);
    let yOff = 0;
    for (const entry of recent) {
      const sigil = entry.side === "hero" ? "▶" : "◀";
      const color = entry.side === "hero" ? "#9ad19a" : "#d19a9a";
      const t = this.add.text(0, yOff, `${sigil} ${entry.text}`, {
        ...TEXT_STYLE.small,
        fontSize: "11px",
        color,
        wordWrap: { width: 290 },
      });
      this.logContainer.add(t);
      this.logTextItems.push(t);
      yOff += t.height + 4;
    }
  }

  private refreshHand() {
    if (!this.handLayer) return;
    this.handLayer.removeAll(true);

    const hand = this.state.heroHand;
    if (hand.length === 0) return;

    const w = this.scale.width;
    const h = this.scale.height;
    const gap = 22;
    const handAreaLeft = 440;
    const handAreaRight = w - 480;
    const availableW = handAreaRight - handAreaLeft;
    const center = (handAreaLeft + handAreaRight) / 2;
    const naturalStep = HERO_CARD_W + gap;
    const minStep = 70;
    const step = hand.length > 1
      ? Math.max(minStep, Math.min(naturalStep, (availableW - HERO_CARD_W) / (hand.length - 1)))
      : 0;
    const totalW = HERO_CARD_W + (hand.length - 1) * step;
    const startX = center - totalW / 2 + HERO_CARD_W / 2;
    const y = h - HERO_CARD_H / 2 - 30;

    for (let i = 0; i < hand.length; i++) {
      const card = hand[i];
      const x = startX + i * step;
      const node = new BattleCard({
        scene: this,
        card,
        canHope: canPlayHeroCard(this.state, i, "hope"),
        canFear: canPlayHeroCard(this.state, i, "fear"),
        width: HERO_CARD_W,
        height: HERO_CARD_H,
        stats: this.state.hero.stats,
        level: this.state.hero.level,
        domainId: this.state.heroDomainId,
        onPlayHope: () => { if (!this.isProcessing) this.onPlayCard(i, "hope"); },
        onPlayFear: () => { if (!this.isProcessing) this.onPlayCard(i, "fear"); },
      });
      node.setPosition(x, y);
      node.setBaseY(y);
      node.setBaseDepth(0);
      node.handIndex = i;
      node.setName(`${card}#${i}`);
      this.handLayer.add(node);
    }
    this.sortHand();
  }

  private refreshEnemyHand() {
    if (!this.enemyHandLayer) return;
    this.enemyHandLayer.removeAll(true);

    const enemyCardCount = 5;
    const w = this.scale.width;
    const gap = 12;
    const handAreaLeft = 440;
    const handAreaRight = w - 480;
    const center = (handAreaLeft + handAreaRight) / 2;
    const totalW = enemyCardCount * ENEMY_CARD_W + (enemyCardCount - 1) * gap;
    const startX = center - totalW / 2 + ENEMY_CARD_W / 2;
    const y = ENEMY_CARD_H / 2 + 30;

    for (let i = 0; i < enemyCardCount; i++) {
      const x = startX + i * (ENEMY_CARD_W + gap);
      const back = this.makeCardBack(ENEMY_CARD_W, ENEMY_CARD_H);
      back.setPosition(x, y);
      this.enemyHandLayer.add(back);
    }
  }

  private makeCardBack(w: number, h: number): Phaser.GameObjects.Container {
    const rex = (this as unknown as RexScene).rexUI;
    const container = this.add.container(0, 0);

    const bg = rex.add.roundRectangle(0, 0, w, h, 10, THEME.bgPanel, 1)
      .setStrokeStyle(2, THEME.borderAccent, 0.7);
    container.add(bg as unknown as Phaser.GameObjects.GameObject);

    const innerBorder = rex.add.roundRectangle(0, 0, w - 16, h - 16, 6)
      .setStrokeStyle(1, THEME.borderAccent, 0.4)
      .setFillStyle(0, 0);
    container.add(innerBorder as unknown as Phaser.GameObjects.GameObject);

    const ornamentTop = this.add.text(0, -h / 2 + 30, "❖", {
      ...TEXT_STYLE.display,
      fontSize: "24px",
      color: `#${THEME.borderAccent.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5);
    ornamentTop.setAlpha(0.6);
    container.add(ornamentTop);

    const rune = this.add.text(0, 0, "✦", {
      ...TEXT_STYLE.display,
      fontSize: "72px",
      color: `#${THEME.borderAccent.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5);
    rune.setAlpha(0.7);
    container.add(rune);

    const ornamentBot = this.add.text(0, h / 2 - 30, "❖", {
      ...TEXT_STYLE.display,
      fontSize: "24px",
      color: `#${THEME.borderAccent.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5);
    ornamentBot.setAlpha(0.6);
    container.add(ornamentBot);

    container.setSize(w, h);
    return container;
  }

  private refreshButtons() {
    if (!this.endTurnBtn) return;
    const activeHero = this.state.activeSide === "hero" && !this.isProcessing;
    this.endTurnBtn.setAlpha(activeHero ? 1 : 0.45);
    this.endTurnBtn.disableInteractive();
    if (activeHero) this.endTurnBtn.setInteractive({ useHandCursor: true });
  }

  private makeButton(label: string, w: number, h: number, primary: boolean, onClick: () => void): Phaser.GameObjects.Container {
    const rex = (this as unknown as RexScene).rexUI;
    const fillColor = primary ? THEME.borderAccent : THEME.bgElevated;
    const textColor = primary ? "#0c0a14" : "#e8e4d6";
    const strokeColor = primary ? 0x8a6a2a : THEME.borderSubtle;

    const bg = rex.add.roundRectangle(0, 0, w, h, 6, fillColor)
      .setStrokeStyle(1, strokeColor, 1);
    const text = this.add.text(0, 0, label.toUpperCase(), {
      ...TEXT_STYLE.display,
      fontSize: "13px",
      fontStyle: "bold",
      color: textColor,
    }).setOrigin(0.5);
    text.setLetterSpacing(2);

    const container = this.add.container(0, 0, [
      bg as unknown as Phaser.GameObjects.GameObject,
      text,
    ]);
    container.setSize(w, h);
    container.setInteractive({ useHandCursor: true });
    container.on("pointerdown", onClick);
    container.on("pointerover", () => {
      bg.setStrokeStyle(2, primary ? THEME.textAccent : THEME.borderAccent, 1);
    });
    container.on("pointerout", () => {
      bg.setStrokeStyle(1, strokeColor, 1);
    });
    return container;
  }

  private onPlayCard(handIndex: number, mode: "hope" | "fear") {
    if (this.state.activeSide !== "hero" || this.isProcessing) return;
    this.isProcessing = true;
    this.refreshButtons();

    const card = this.state.heroHand[handIndex];
    const result = playHeroCard(this.state, handIndex, mode);
    if (card) this.showPlayedCard(card, mode, "hero");

    this.state = consumeHeroCard(this.state, handIndex, mode);
    this.refreshAll();

    this.playRollSequence(result, () => {
      this.dismissPlayedCard(() => {
        const hpBefore = this.state.hero.hp;
        this.state = result.state;
        if (this.state.hero.hp > hpBefore) this.flashScreen(THEME.success, 0.18);
        this.refreshAll();
        this.isProcessing = false;
        this.afterAction();
      });
    });
  }

  private onEndTurn() {
    if (this.state.activeSide !== "hero" || this.isProcessing) return;
    this.isProcessing = true;
    this.state = endHeroTurn(this.state);
    this.refreshAll();

    this.time.delayedCall(350, () => {
      const turnResult = enemyTurn(this.state);
      let stopped = false;
      const steps: Array<(next: () => void) => void> = [];
      for (const action of turnResult.actions) {
        const a = action;
        steps.push((next) => { if (stopped) return next(); this.showPlayedCard(a.card, a.mode, "enemy"); this.time.delayedCall(420, next); });
        steps.push((next) => { if (stopped) return next(); this.showDice2d12(a.diceRolls.hope, a.diceRolls.fear, a.mode, a.modeCrit, a.hitResult, next); });
        if (a.hitResult?.hit) {
          if (a.damageRolls.length > 0) {
            for (const roll of a.damageRolls) {
              steps.push((next) => { if (stopped) return next(); this.showDamageDiceAnimation(roll.rolls, roll.notation.sides, roll.sum, a.damageDealt, next); });
            }
          } else if (a.damageDealt > 0) {
            steps.push((next) => { if (stopped) return next(); this.showFixedDamageAnimation(a.damageDealt, a.hitResult!.crit, next); });
          }
        }
        steps.push((next) => {
          if (stopped) return next();
          const hpBefore = this.state.hero.hp;
          this.state = a.stateAfter;
          const hpAfter = this.state.hero.hp;
          if (hpAfter < hpBefore) this.flashScreen(THEME.fear, 0.3);
          this.refreshAll();
          if (isGameOver(this.state)) stopped = true;
          this.dismissPlayedCard(next);
        });
      }
      const finish = () => {
        if (!isGameOver(this.state)) {
          this.state = startHeroTurn(this.state);
        }
        this.refreshAll();
        this.isProcessing = false;
        this.afterAction();
      };
      const runNext = () => {
        const step = steps.shift();
        if (step) step(runNext); else finish();
      };
      runNext();
    });
  }

  private onRetreat() {
    const heroXp = this.app.getWorldState()?.hero.xp ?? 0;
    const reward: BattleReward = {
      won: false,
      heroHpAfter: this.state.hero.hp,
      heroFearAfter: this.state.heroFearAccumulated,
      heroXpAfter: heroXp,
      xpGained: 0, reputationGained: 0, goldGained: 0,
    };
    this.app.resolveBattle(reward);
    this.app.completeCurrentEvent();
  }

  private afterAction() {
    const over = isGameOver(this.state);
    this.refreshAll();
    if (over === "hero_won") return this.showVictory();
    if (over === "enemy_won") return this.showDefeat();
    this.maybeAutoEndTurn();
  }

  private maybeAutoEndTurn() {
    if (this.state.activeSide !== "hero" || this.isProcessing) return;
    const hand = this.state.heroHand;
    const anyPlayable = hand.some((_, i) =>
      canPlayHeroCard(this.state, i, "hope") || canPlayHeroCard(this.state, i, "fear")
    );
    if (anyPlayable) return;

    this.state.log.push({
      turn: this.state.turn,
      side: "hero",
      text: "Маны нет — ход передан автоматически.",
    });
    this.refreshLog();

    this.time.delayedCall(900, () => {
      if (!this.isProcessing && this.state.activeSide === "hero") this.onEndTurn();
    });
  }

  private playRollSequence(result: { hitResult?: HitResult; damageRolls: DamageRollInfo[]; damageDealt: number }, cb: () => void) {
    const steps: Array<(next: () => void) => void> = [];
    if (result.hitResult) {
      const hit = result.hitResult;
      steps.push((next) => this.showHitAnimation(hit, next));
    }
    if (result.hitResult?.hit) {
      if (result.damageRolls.length > 0) {
        for (const roll of result.damageRolls) {
          steps.push((next) => this.showDamageDiceAnimation(roll.rolls, roll.notation.sides, roll.sum, result.damageDealt, next));
        }
      } else if (result.damageDealt > 0) {
        steps.push((next) => this.showFixedDamageAnimation(result.damageDealt, result.hitResult?.crit ?? false, next));
      }
    }
    if (steps.length === 0) {
      steps.push((next) => this.time.delayedCall(1400, next));
    }
    const runNext = () => {
      const step = steps.shift();
      if (step) step(runNext); else cb();
    };
    runNext();
  }

  private showPlayedCard(card: import("../core/roscha").CardDef, mode: "hope" | "fear", side: "hero" | "enemy") {
    if (this.playedCardOverlay) this.playedCardOverlay.destroy();
    const w = this.scale.width, h = this.scale.height;

    const targetX = w / 2 - 460;
    const targetY = h / 2;

    const owner = side === "enemy" ? this.state.enemy : this.state.hero;
    const node = new BattleCard({
      scene: this,
      card,
      canHope: true,
      canFear: true,
      width: HERO_CARD_W,
      height: HERO_CARD_H,
      stats: owner.stats,
      level: owner.level,
      domainId: side === "hero" ? this.state.heroDomainId : undefined,
      onPlayHope: () => {},
      onPlayFear: () => {},
    });
    void mode;
    this.add.existing(node);
    node.setDepth(8200);
    node.disableInteractive();

    this.playedCardOverlay = node as unknown as Phaser.GameObjects.Container;

    const startY = side === "hero" ? h - 80 : 80;
    node.setPosition(targetX, startY).setScale(0.5).setAlpha(0);
    this.tweens.add({
      targets: node, x: targetX, y: targetY, scale: 1, alpha: 1,
      duration: 320, ease: "Back.easeOut",
    });
  }

  private dismissPlayedCard(cb: () => void) {
    if (!this.playedCardOverlay) { cb(); return; }
    const box = this.playedCardOverlay;
    this.tweens.add({
      targets: box, x: box.x + 60, alpha: 0, scale: 0.7,
      duration: 220, ease: "Sine.easeIn",
      onComplete: () => {
        box.destroy();
        if (this.playedCardOverlay === box) this.playedCardOverlay = undefined;
        cb();
      },
    });
  }

  private flashScreen(color: number, alpha: number) {
    const w = this.scale.width, h = this.scale.height;
    const flash = this.add.rectangle(0, 0, w, h, color, alpha).setOrigin(0, 0).setDepth(9000);
    this.tweens.add({
      targets: flash, alpha: 0, duration: 450, ease: "Sine.easeOut",
      onComplete: () => flash.destroy(),
    });
  }

  private showHitAnimation(hit: HitResult, cb: () => void) {
    const verdict = hit.crit ? "КРИТ" : (hit.hit ? "ПОПАЛ" : "МИМО");
    const accent = hit.crit ? THEME.textAccent : (hit.hit ? THEME.success : THEME.fear);
    const color = accent;

    if (hit.enemyRolls) {
      const a = hit.enemyRolls.a;
      const b = hit.enemyRolls.b;
      this.spawnMultiDiceOverlay({
        title: `Враг бросает 2d12 на попадание (+${hit.roll.modifier} vs Укл ${hit.targetEvasion})`,
        dice: [
          { label: "d12", sides: 12, finalValue: a, color, highlight: hit.crit },
          { label: "d12", sides: 12, finalValue: b, color, highlight: hit.crit },
        ],
        bgWidth: 600,
        subline: `${a} + ${b} + ${hit.roll.modifier} = ${hit.roll.total}    ${verdict}`,
        accent,
        cb,
        bothHighlight: hit.crit,
      });
      return;
    }

    this.spawnMultiDiceOverlay({
      title: `d20 + ${hit.roll.modifier} против Уклонения ${hit.targetEvasion}`,
      dice: [
        { label: "АТАКА", sides: 20, finalValue: hit.roll.raw, color, highlight: hit.hit },
      ],
      bgWidth: 460,
      subline: `${hit.roll.raw} + ${hit.roll.modifier} = ${hit.roll.total}    ${verdict}`,
      accent,
      cb,
    });
  }

  private showDamageDiceAnimation(rolls: number[], sides: number, sum: number, finalDmg: number, cb: () => void) {
    const dice = rolls.map<DieDef>((r) => ({ label: `d${sides}`, sides, finalValue: r, color: THEME.danger, highlight: false }));
    const subline = rolls.length > 1
      ? `${rolls.join("+")} = ${sum} → ${finalDmg}`
      : `${sum} → ${finalDmg}`;
    this.spawnMultiDiceOverlay({
      title: `Урон ${rolls.length}d${sides}`,
      dice,
      bgWidth: Math.max(420, 140 + rolls.length * 110),
      subline,
      accent: THEME.danger,
      cb,
    });
  }

  private showFixedDamageAnimation(total: number, crit: boolean, cb: () => void) {
    const title = "Урон";
    const subline = crit ? "КРИТ ×2" : "";
    const accent = crit ? THEME.textAccent : THEME.danger;
    this.showDiceOverlay(title, 0, `${total}`, subline, accent, cb);
  }

  private showDice2d12(hope: number, fear: number, mode: "hope" | "fear", crit: boolean, hit: HitResult | undefined, cb: () => void) {
    const accent = crit ? THEME.textAccent : (mode === "hope" ? THEME.hope : THEME.fear);
    const modeLabel = crit ? "ДУБЛЬ — КРИТ" : (mode === "hope" ? "→ НАДЕЖДА" : "→ СТРАХ");
    let title = "Враг бросает 2d12";
    let subline = modeLabel;
    if (hit) {
      const verdict = hit.crit ? "КРИТ" : (hit.hit ? "ПОПАЛ" : "МИМО");
      title = `Враг бросает 2d12 (+${hit.roll.modifier} vs Укл ${hit.targetEvasion})`;
      subline = `${modeLabel}   |   ${hope}+${fear}+${hit.roll.modifier}=${hit.roll.total}   ${verdict}`;
    }
    this.spawnMultiDiceOverlay({
      title,
      dice: [
        { label: "НАДЕЖДА", sides: 12, finalValue: hope, color: THEME.hope, highlight: mode === "hope" && !crit, fade: !crit && mode !== "hope" },
        { label: "СТРАХ",   sides: 12, finalValue: fear, color: THEME.fear, highlight: mode === "fear" && !crit, fade: !crit && mode !== "fear" },
      ],
      bgWidth: 640,
      subline,
      accent,
      cb,
      bothHighlight: crit,
    });
  }

  private spawnMultiDiceOverlay(opts: {
    title: string;
    dice: DieDef[];
    bgWidth: number;
    subline: string;
    accent: number;
    cb: () => void;
    bothHighlight?: boolean;
  }) {
    const rex = (this as unknown as RexScene).rexUI;
    const w = this.scale.width, h = this.scale.height;

    if (this.diceOverlay) this.diceOverlay.destroy();
    const box = this.add.container(w / 2, h / 2).setDepth(8000);
    this.diceOverlay = box;

    const bg = rex.add.roundRectangle(0, 0, opts.bgWidth, 200, 16, THEME.bgPanel, 0.96)
      .setStrokeStyle(2, opts.accent, 0.9);
    box.add(bg as unknown as Phaser.GameObjects.GameObject);

    const titleText = this.add.text(0, -78, opts.title, {
      ...TEXT_STYLE.small, fontSize: "12px", fontStyle: "italic",
    }).setOrigin(0.5);
    titleText.setLetterSpacing(1);
    box.add(titleText);

    const n = opts.dice.length;
    const spacing = n <= 2 ? 90 : Math.min(80, (opts.bgWidth - 100) / n);
    const startX = -((n - 1) / 2) * (n <= 2 ? 180 : spacing * 2);

    const tray = opts.dice.map((d, i) => {
      const x = n <= 2 ? (n === 1 ? 0 : (i === 0 ? -90 : 90)) : startX + i * spacing * 2;
      const trayContainer = this.add.container(x, 0);
      const labelText = this.add.text(0, -36, d.label, {
        ...TEXT_STYLE.small, fontSize: "11px", fontStyle: "bold",
        color: `#${d.color.toString(16).padStart(6, "0")}`,
      }).setOrigin(0.5);
      labelText.setLetterSpacing(2);
      trayContainer.add(labelText);
      const numberText = this.add.text(0, 4, "?", {
        ...TEXT_STYLE.display, fontSize: n <= 2 ? "48px" : "36px", fontStyle: "bold",
        color: `#${d.color.toString(16).padStart(6, "0")}`,
      }).setOrigin(0.5);
      numberText.setShadow(0, 2, "#000000", 6, false, true);
      trayContainer.add(numberText);
      box.add(trayContainer);
      return { def: d, tray: trayContainer, numberText };
    });

    const sublineText = this.add.text(0, 70, "", {
      ...TEXT_STYLE.small, fontSize: "13px", fontStyle: "bold",
      color: `#${opts.accent.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5).setAlpha(0);
    sublineText.setLetterSpacing(2);
    box.add(sublineText);

    box.setScale(0.6).setAlpha(0);
    this.tweens.add({ targets: box, scale: 1, alpha: 1, duration: 200, ease: "Back.easeOut" });

    let elapsed = 0;
    const rollDuration = 1400;
    const spin = this.time.addEvent({
      delay: 60, loop: true,
      callback: () => {
        elapsed += 60;
        if (elapsed < rollDuration) {
          for (const t of tray) {
            t.numberText.setText(`${1 + Math.floor(Math.random() * t.def.sides)}`);
            t.tray.setAngle((Math.random() - 0.5) * 18);
          }
        } else {
          spin.remove(false);
          for (const t of tray) {
            t.numberText.setText(`${t.def.finalValue}`);
            t.tray.setAngle(0);
            if (t.def.fade) {
              this.tweens.add({ targets: t.tray, alpha: 0.4, duration: 280 });
            } else if (t.def.highlight || opts.bothHighlight) {
              this.tweens.add({ targets: t.tray, scale: opts.bothHighlight ? 1.15 : 1.2, duration: 280, ease: "Back.easeOut" });
            }
          }

          sublineText.setText(opts.subline);
          this.tweens.add({ targets: sublineText, alpha: 1, duration: 240, delay: 160, ease: "Sine.easeOut" });

          this.time.delayedCall(2200, () => {
            this.tweens.add({
              targets: box, scale: 0.7, alpha: 0, duration: 180,
              onComplete: () => {
                box.destroy();
                if (this.diceOverlay === box) this.diceOverlay = undefined;
                opts.cb();
              },
            });
          });
        }
      },
    });
  }

  private showDiceOverlay(title: string, sides: number, mainNumber: string, subline: string, accent: number, cb: () => void) {
    const rex = (this as unknown as RexScene).rexUI;
    const w = this.scale.width, h = this.scale.height;

    if (this.diceOverlay) this.diceOverlay.destroy();
    const box = this.add.container(w / 2, h / 2).setDepth(8000);
    this.diceOverlay = box;

    const bg = rex.add.roundRectangle(0, 0, 520, 170, 16, THEME.bgPanel, 0.96)
      .setStrokeStyle(2, accent, 0.9);
    box.add(bg as unknown as Phaser.GameObjects.GameObject);

    const titleText = this.add.text(0, -56, title, {
      ...TEXT_STYLE.small, fontSize: "12px", fontStyle: "italic",
    }).setOrigin(0.5);
    titleText.setLetterSpacing(1);
    box.add(titleText);

    const tray = this.add.container(0, 4);
    box.add(tray);

    const numberText = this.add.text(0, 0, "?", {
      ...TEXT_STYLE.display, fontSize: "52px", fontStyle: "bold",
      color: `#${accent.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5);
    numberText.setShadow(0, 2, "#000000", 6, false, true);
    tray.add(numberText);

    const sublineText = this.add.text(0, 56, "", {
      ...TEXT_STYLE.small, fontSize: "13px", fontStyle: "bold",
      color: `#${accent.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5).setAlpha(0);
    sublineText.setLetterSpacing(1.5);
    box.add(sublineText);

    box.setScale(0.6).setAlpha(0);
    this.tweens.add({ targets: box, scale: 1, alpha: 1, duration: 200, ease: "Back.easeOut" });

    const land = () => {
      numberText.setText(mainNumber);
      tray.setScale(0.4).setAngle(0).setPosition(0, 4);
      this.tweens.add({ targets: tray, scale: 1, duration: 320, ease: "Back.easeOut" });
      if (subline) {
        this.tweens.add({ targets: sublineText, alpha: 1, y: 60, duration: 240, delay: 160, ease: "Sine.easeOut" });
        sublineText.setText(subline);
      }
      this.time.delayedCall(2000, () => {
        this.tweens.add({
          targets: box, scale: 0.7, alpha: 0, duration: 180,
          onComplete: () => {
            box.destroy();
            if (this.diceOverlay === box) this.diceOverlay = undefined;
            cb();
          },
        });
      });
    };

    if (sides <= 0) {
      land();
      return;
    }

    let elapsed = 0;
    const rollDuration = 1400;
    const spin = this.time.addEvent({
      delay: 60, loop: true,
      callback: () => {
        elapsed += 60;
        if (elapsed < rollDuration) {
          numberText.setText(`${1 + Math.floor(Math.random() * sides)}`);
          tray.setAngle((Math.random() - 0.5) * 24);
          tray.setPosition((Math.random() - 0.5) * 6, 4 + (Math.random() - 0.5) * 6);
        } else {
          spin.remove(false);
          land();
        }
      },
    });
  }

  private showVictory() {
    const rex = (this as unknown as RexScene).rexUI;
    const w = this.scale.width, h = this.scale.height;
    const enemy = ENEMY_TEMPLATES[this.battlePayload.enemyTemplateId];
    const heroWorld = this.app.getWorldState()?.hero;

    const cardPool = heroWorld?.classId ? [...cardsByClass(heroWorld.classId), ...commonCards()] : [...commonCards()];
    const loot = rollLoot(getLootTable(enemy.lootTableId), undefined, heroWorld?.reputationUpgrades, cardPool);
    const currentXp = heroWorld?.xp ?? 0;
    const currentLevel = heroWorld?.level ?? 1;
    const xpAmount = this.battlePayload.xpReward ?? enemy.xpReward;
    const xpResult = grantXp(currentXp, currentLevel, xpAmount);

    const reward: BattleReward = {
      won: true,
      heroHpAfter: this.state.hero.hp,
      heroFearAfter: this.state.heroFearAccumulated,
      heroXpAfter: xpResult.newXp,
      xpGained: xpAmount,
      reputationGained: enemy.reputationReward,
      goldGained: loot.goldAmount ?? 0,
      cardChoicePool: loot.cardChoicePool,
      cardPick: this.battlePayload.cardPick,
      equipmentAdded: loot.equipmentId,
    };

    const lines: string[] = [];
    lines.push(`+${xpAmount} XP` + (xpResult.newLevel !== currentLevel ? "   ★ НОВЫЙ УРОВЕНЬ" : ""));
    lines.push(`+${enemy.reputationReward} репутации`);
    if (reward.goldGained > 0) lines.push(`+${reward.goldGained} золота`);
    if (reward.cardChoicePool && reward.cardChoicePool.length > 0) {
      lines.push("+ выбор карты");
    }
    if (reward.equipmentAdded) {
      lines.push(`+ снаряжение: ${reward.equipmentAdded}`);
    }
    if (lines.length === 2 && !reward.goldGained) lines.push(`Лут: ${loot.row.label}`);

    const adminEffect = this.battlePayload.effect;
    if (adminEffect) {
      lines.push(`★ ${describeBattleEffect(adminEffect)}`);
    }

    this.resultOverlay = this.add.container(w / 2, h / 2).setDepth(8500);

    const dim = this.add.rectangle(0, 0, w, h, 0x000000, 0.7).setOrigin(0.5);
    this.resultOverlay.add(dim);

    const panel = rex.add.roundRectangle(0, 0, 480, 360, 16, THEME.bgPanel)
      .setStrokeStyle(2, THEME.borderAccent, 1);
    this.resultOverlay.add(panel as unknown as Phaser.GameObjects.GameObject);

    const title = this.add.text(0, -130, "ПОБЕДА", {
      ...TEXT_STYLE.display, fontSize: "38px", fontStyle: "bold",
    }).setOrigin(0.5);
    title.setLetterSpacing(8);
    this.resultOverlay.add(title);

    const sub = this.add.text(0, -86, `${enemy.name} повержен`, {
      ...TEXT_STYLE.small, fontSize: "12px", fontStyle: "italic",
    }).setOrigin(0.5);
    sub.setLetterSpacing(1);
    this.resultOverlay.add(sub);

    const sep = this.add.rectangle(0, -64, 320, 1, THEME.borderSubtle, 0.7);
    this.resultOverlay.add(sep);

    const rewardsText = this.add.text(0, -42, lines.join("\n"), {
      ...TEXT_STYLE.body, fontSize: "14px", align: "center", lineSpacing: 8,
    }).setOrigin(0.5, 0);
    this.resultOverlay.add(rewardsText);

    const btn = this.makeButton("В путь", 180, 44, true, () => this.finishVictory(reward));
    btn.setPosition(0, 130);
    this.resultOverlay.add(btn);
  }

  private finishVictory(reward: BattleReward) {
    this.app.resolveBattle(reward);
    if (reward.goldGained > 0) this.app.emitFloatingReward(floatingFromResource("gold", reward.goldGained));
    if (reward.xpGained > 0) this.app.emitFloatingReward(floatingXp(reward.xpGained));
    if (reward.reputationGained > 0) this.app.emitFloatingReward(floatingReputation(reward.reputationGained));
    const adminEffects: RewardEffect[] = this.battlePayload.effects ?? (this.battlePayload.effect ? [this.battlePayload.effect] : []);
    for (const eff of adminEffects) {
      this.app.applyRewardEffect(eff);
      const f = floatingFromEffect(eff);
      if (f) this.app.emitFloatingReward(f);
    }
  }

  private showDefeat() {
    const rex = (this as unknown as RexScene).rexUI;
    const w = this.scale.width, h = this.scale.height;

    this.resultOverlay = this.add.container(w / 2, h / 2).setDepth(8500);
    const dim = this.add.rectangle(0, 0, w, h, 0x000000, 0.75).setOrigin(0.5);
    this.resultOverlay.add(dim);

    const panel = rex.add.roundRectangle(0, 0, 440, 240, 16, THEME.bgPanel)
      .setStrokeStyle(2, THEME.fear, 1);
    this.resultOverlay.add(panel as unknown as Phaser.GameObjects.GameObject);

    const title = this.add.text(0, -60, "ПОРАЖЕНИЕ", {
      ...TEXT_STYLE.display, fontSize: "32px", fontStyle: "bold",
      color: `#${THEME.fear.toString(16).padStart(6, "0")}`,
    }).setOrigin(0.5);
    title.setLetterSpacing(6);
    this.resultOverlay.add(title);

    const sub = this.add.text(0, -16, "Ты пал в бою", {
      ...TEXT_STYLE.small, fontSize: "13px", fontStyle: "italic",
    }).setOrigin(0.5);
    this.resultOverlay.add(sub);

    const heroXp = this.app.getWorldState()?.hero.xp ?? 0;
    const defeatReward: BattleReward = {
      won: false,
      heroHpAfter: Math.max(1, Math.floor(this.state.hero.maxHp * 0.3)),
      heroFearAfter: this.state.heroFearAccumulated,
      heroXpAfter: heroXp,
      xpGained: 0, reputationGained: 0, goldGained: 0,
    };
    const btn = this.makeButton("На карту", 160, 42, true, () => {
      this.app.resolveBattle(defeatReward);
      this.app.backToMap();
    });
    btn.setPosition(0, 70);
    this.resultOverlay.add(btn);
  }

  private onResize = () => {
    const w = this.scale.width, h = this.scale.height;
    this.bgGradient?.clear();
    this.bgGradient?.fillStyle(THEME.bgDeep, 1).fillRect(0, 0, w, h);
    this.refreshHand();
    void this.enemyPanel;
    void this.heroPanel;
    void this.enemyPanelBg;
    void this.heroPanelBg;
  };

  shutdown() {
    this.scale.off("resize", this.onResize, this);
    this.input.off("pointermove", this.handlePointerMove, this);
    this.listenersBound = false;
    this.enemyHp?.destroy();
    this.heroHp?.destroy();
    this.enemyHp = undefined;
    this.heroHp = undefined;
    this.children.removeAll(true);
  }
}
